import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMapPin, FiMail, FiBookOpen, FiUsers, FiCheck } = FiIcons;

const Section = ({ id, children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 50 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`py-12 ${className}`}
    >
      {children}
    </motion.section>
  );
};

// Utility component for list items
const PolicyListItem = ({ children }) => (
  <li className="flex items-start mt-2">
    <SafeIcon icon={FiCheck} className="flex-shrink-0 w-5 h-5 mt-1 mr-2 text-purple-600" />
    <p className="text-gray-700">{children}</p>
  </li>
);

// Helper function to render policy text with paragraphs
const renderPolicyText = (text, isListItem = false) => {
  // Split the text by double newlines or single newlines followed by a bullet point or number
  const paragraphs = text.split(/\n\n|\n[●\d]/).filter(p => p.trim() !== '');

  return paragraphs.map((paragraph, index) => {
    const trimmed = paragraph.trim();
    if (trimmed.startsWith('●')) {
      // Handle bullet points
      const bullets = trimmed.split('\n').map(b => b.trim().replace(/^●\s*/, '')).filter(b => b);
      return (
        <ul key={index} className="list-disc ml-6 space-y-2 mb-4">
          {bullets.map((bullet, bIndex) => (
            <li key={bIndex} className="text-gray-700">{bullet}</li>
          ))}
        </ul>
      );
    } else if (trimmed.startsWith('Note:')) {
      // Custom note block
      return (
        <div key={index} className="px-4 py-3 my-4 border-l-4 border-yellow-500 bg-yellow-50 text-yellow-800 rounded-md">
          <p className="font-medium">{trimmed}</p>
        </div>
      );
    } else if (trimmed.startsWith('Important:')) {
      // Custom important block
      return (
        <div key={index} className="px-4 py-3 my-4 border-l-4 border-purple-500 bg-purple-50 text-purple-800 rounded-md">
          <p className="font-medium">{trimmed}</p>
        </div>
      );
    } else if (trimmed.startsWith('Be cautious:')) {
      // Custom caution block
      return (
        <div key={index} className="px-4 py-3 my-4 border-l-4 border-red-500 bg-red-50 text-red-800 rounded-md">
          <p className="font-medium">{trimmed}</p>
        </div>
      );
    } else if (trimmed.startsWith('1.') || trimmed.startsWith('2.') || trimmed.startsWith('3.') || trimmed.startsWith('4.') || trimmed.startsWith('5.') || trimmed.startsWith('6.')) {
      // Handle numbered lists or principles
      const items = trimmed.split('\n').map(i => i.trim()).filter(i => i.startsWith(`${i.split('.')[0]}.`));
      return (
        <ol key={index} className="list-decimal ml-6 space-y-2 mb-4">
          {items.map((item, iIndex) => (
            <li key={iIndex} className="text-gray-700">{item}</li>
          ))}
        </ol>
      );
    }
    
    return <p key={index} className={`text-gray-700 leading-relaxed mb-4 ${isListItem ? 'ml-0' : ''}`}>{trimmed}</p>;
  });
};

const PrivacyPolicy = () => {
  const policyContent = [
    {
      title: 'ABOUT US AND THIS POLICY',
      id: 'about-us',
      sections: [
        {
          subtitle: 'Who We Are',
          content: `TOGETHR SPACE LTD ("we", "us", "our") operates the website www.togethr.space and mobile application (together, the "Platform"). We are a company incorporated in England and Wales under company number 13917780, with our registered office at 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom.`,
        },
        {
          subtitle: 'Our Commitment to Your Privacy',
          content: `We are deeply committed to protecting the privacy and safety of our community members, particularly recognising the unique privacy needs of LGBTQIA+ individuals. We comply with:
● The UK General Data Protection Regulation (UK GDPR)
● The Data Protection Act 2018 (DPA 2018)
● The Privacy and Electronic Communications Regulations 2003 (PECR)
● All other applicable UK data protection legislation`,
        },
        {
          subtitle: 'Your Data Controller',
          content: `When you use our Platform, we act as the "data controller" of your personal data. This means we decide how and why your personal data is processed, and we are responsible for ensuring it is used lawfully, fairly, and transparently.`,
        },
        {
          subtitle: 'Changes to This Policy',
          content: `We may update this policy from time to time to reflect changes in law or our practices. When we make material changes, we will:
● Update the "Last updated" date at the top of this policy
● Notify you via email (if you have provided one) or through prominent notices on the Platform
● In some cases, seek your renewed consent where required by law
We encourage you to review this policy periodically.`,
        },
      ],
    },
    {
      title: 'CONTACT INFORMATION',
      id: 'contact-info',
      sections: [
        {
          subtitle: 'Data Protection Contact',
          content: `Name: Kevin Leonce
Email: hi@togethr.space
Address: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom

If you have any questions, concerns, or complaints about how we handle your personal data, please contact us using the details above. We aim to respond to all inquiries within 30 days.`,
        },
      ],
    },
    {
      title: 'WHAT INFORMATION WE COLLECT',
      id: 'data-collection',
      sections: [
        {
          subtitle: 'Information You Provide Directly',
          content: `Account Information
When you create an account, we collect:
● Name (you may use a preferred name or pseudonym)
● Email address
● Date of birth (to ensure you are 18 or older)
● Username
● Password (stored in encrypted form)
● Profile information (optional: pronouns, location, bio, profile photo)

Community Engagement Information
When you use our Platform features, we may collect:
● Posts, comments, and messages you create
● Event registrations and attendance
● Community group memberships
● Responses to surveys or feedback forms
● Customer support correspondence

Optional Sensitive Information
You may choose to share information about:
● Sexual orientation
● Gender identity
● Pronouns
● Relationship status

Important: Information about your sexual orientation and gender identity is "special category data" under UK GDPR, which receives enhanced legal protection. We will only process this information with your explicit consent, which you can withdraw at any time.`,
        },
        {
          subtitle: 'Information We Collect Automatically',
          content: `Technical Information
When you access our Platform, we automatically collect:
● IP address
● Device type and operating system
● Browser type and version
● Time zone settings
● Device identifiers (such as mobile advertising IDs)

Usage Information
We track how you interact with our Platform:
● Pages or features you access
● Time spent on pages
● Links clicked
● Search queries
● Access times and dates

Location Information
With your permission, we may collect:
● Precise geolocation data (via GPS)
● Approximate location (derived from IP address)

You can control location permissions through your device settings.`,
        },
        {
          subtitle: 'Information from Third Parties',
          content: `Social Media Integration
If you connect your account with social media platforms (e.g., Facebook, Instagram), we may receive:
● Basic profile information
● Email address
● Friends list (only with additional permission)

Payment Processors
If you make purchases, our payment processors share:
● Transaction confirmation
● Payment method type (not full card details)`,
        },
      ],
    },
    {
      title: 'HOW WE USE YOUR INFORMATION',
      id: 'data-use',
      sections: [
        {
          subtitle: 'To Provide and Improve Our Services',
          content: `● Creating and managing your account
● Enabling you to connect with other community members
● Personalising your experience on the Platform
● Processing transactions for paid services
● Providing customer support
● Improving and developing new features`,
        },
        {
          subtitle: 'For Safety and Security',
          content: `● Verifying your identity and age
● Detecting and preventing fraud, abuse, and violations of our Terms of Service
● Protecting the safety of our community members
● Complying with legal obligations
● Responding to law enforcement requests where legally required`,
        },
        {
          subtitle: 'For Communications',
          content: `● Sending service-related notifications (e.g., account updates, security alerts)
● Sending marketing communications only if you have opted in
● Responding to your inquiries
● Conducting surveys and research (with your consent)

You can opt out of marketing emails at any time by clicking the "unsubscribe" link in any marketing email or by contacting us.`,
        },
        {
          subtitle: 'For Analytics and Research',
          content: `● Understanding how our Platform is used
● Analysng trends and user preferences
● Conducting research to improve services for LGBTQIA+ communities
● Creating aggregated, anonymised statistics (which cannot identify you)`,
        },
        {
          subtitle: "What We DON'T Do",
          content: `We never:
● Sell your personal data to third parties
● Share your sexual orientation or gender identity without your explicit permission
● Use your data in ways that could "out" you to others without your control
● Share data with governments or organisations that persecute LGBTQIA+ individuals`,
        },
      ],
    },
    {
      title: 'LEGAL BASIS FOR PROCESSING',
      id: 'legal-basis',
      sections: [
        {
          subtitle: 'Consent',
          content: `For processing special category data (sexual orientation, gender identity) and for marketing communications. You can withdraw consent at any time through your account settings or by contacting us.`,
        },
        {
          subtitle: 'Contractual Necessity',
          content: `To provide the services you've signed up for, we need to process certain data (account details, usage information).`,
        },
        {
          subtitle: 'Legitimate Interests',
          content: `We process some data based on our legitimate business interests, such as:
● Improving our Platform
● Detecting and preventing fraud
● Network and information security
● Understanding how our services are used

We always balance our interests against your rights and freedoms.`,
        },
        {
          subtitle: 'Legal Obligations',
          content: `Sometimes we must process data to comply with the law (e.g., age verification, responding to court orders, tax obligations).

See Section 18 for a complete breakdown of lawful bases by data type.`,
        },
      ],
    },
    {
      title: 'HOW WE SHARE YOUR INFORMATION',
      id: 'data-sharing',
      sections: [
        {
          subtitle: 'Service Providers',
          content: `We work with trusted third-party service providers who process data on our behalf:
● Cloud hosting providers (data storage and infrastructure)
● Email service providers (transactional and marketing emails)
● Analytics providers (Google Analytics, with IP anonymisation enabled)
● Payment processors (Stripe, PayPal - they handle payment details directly)
● Customer support tools (helpdesk software)
● Security and fraud prevention services

All service providers:
● Are contractually bound to protect your data (Data Processing Agreements)
● Can only use your data as we instruct them
● Must implement appropriate security measures
● Are selected based on their privacy and security practices`,
        },
        {
          subtitle: 'Other Community Members',
          content: `Some information you provide is visible to other users:
● Your username and profile information you choose to make public
● Posts, comments, and content you share in public areas
● Event attendance (if you choose to make it visible)

You control what's public through your privacy settings. Your sexual orientation and gender identity are never made public without your explicit permission.`,
        },
        {
          subtitle: 'Legal Requirements',
          content: `We may disclose your data if required by law:
● To comply with court orders, warrants, or subpoenas
● To respond to lawful requests from law enforcement
● To protect rights, property, or safety of our company, users, or the public
● To enforce our Terms of Service

Important for LGBTQIA+ Safety: We will vigorously challenge any law enforcement requests that we believe are unjust or could endanger LGBTQIA+ individuals. We will notify you of requests for your data unless legally prohibited.`,
        },
        {
          subtitle: 'Business Transfers',
          content: `If we are involved in a merger, acquisition, or sale of assets, your data may be transferred. We will:
● Notify you via email and/or Platform notice
● Ensure the new owner continues to protect your data under this policy
● Give you the opportunity to delete your account before transfer`,
        },
        {
          subtitle: 'Aggregated or Anonymised Data',
          content: `We may share aggregated, anonymised statistics about our users (e.g., "80% of users are aged 18-30") that cannot identify any individual.`,
        },
        {
          subtitle: 'With Your Consent',
          content: `We may share your data in other ways with your explicit permission.`,
        },
      ],
    },
    {
      title: 'INTERNATIONAL DATA TRANSFERS',
      id: 'data-transfer',
      sections: [
        {
          subtitle: 'Where We Store Your Data',
          content: `Our servers are primarily located in the UK and European Economic Area (EEA). However, some of our service providers may process data outside the UK/EEA.`,
        },
        {
          subtitle: 'Transfer Safeguards',
          content: `When we transfer data internationally, we ensure it is protected through:
● UK GDPR-approved Standard Contractual Clauses
● Adequacy decisions (transfers to countries the UK government recognises as providing adequate protection)
● Additional security measures for high-risk transfers`,
        },
        {
          subtitle: 'Special Protections for LGBTQIA+ Data',
          content: `We will never knowingly transfer sexual orientation or gender identity data to countries where LGBTQIA+ individuals face legal persecution or the death penalty. If you have concerns about international transfers affecting your safety, please contact us.`,
        },
      ],
    },
    {
      title: 'YOUR RIGHTS AND CHOICES',
      id: 'user-rights',
      sections: [
        {
          subtitle: 'Right to Access (Subject Access Request)',
          content: `You can request a copy of all personal data we hold about you. We will provide this free of charge in a commonly used electronic format within one month.`,
        },
        {
          subtitle: 'Right to Rectification',
          content: `If your data is inaccurate or incomplete, you can update it through your account settings or request that we correct it.`,
        },
        {
          subtitle: 'Right to Erasure ("Right to be Forgotten")',
          content: `You can request deletion of your personal data in certain circumstances:
● The data is no longer necessary for the purposes we collected it
● You withdraw consent (for consent-based processing)
● You object to processing based on legitimate interests
● The data was unlawfully processed

Note: We may need to retain some data to comply with legal obligations or establish/defend legal claims.`,
        },
        {
          subtitle: 'Right to Restriction',
          content: `You can ask us to pause processing your data in certain situations:
● You contest the accuracy of the data
● Processing is unlawful, but you don't want erasure
● We no longer need the data, but you need it for legal claims
● You've objected to processing while we verify our legitimate grounds`,
        },
        {
          subtitle: 'Right to Data Portability',
          content: `You can receive your data in a structured, machine-readable format (e.g., JSON, CSV) and transfer it to another service.`,
        },
        {
          subtitle: 'Right to Object',
          content: `You can object to processing based on legitimate interests or for direct marketing purposes. For marketing, we will stop immediately. For other processing, we'll stop unless we have compelling legitimate grounds.`,
        },
        {
          subtitle: 'Rights Related to Automated Decision-Making',
          content: `We do not currently use fully automated decision-making or profiling that produces legal or similarly significant effects. If this changes, we will update this policy and provide you with appropriate rights.`,
        },
        {
          subtitle: 'Right to Withdraw Consent',
          content: `Where we process data based on consent, you can withdraw it at any time through:
● Account settings
● Clicking "unsubscribe" in emails
● Contacting us directly`,
        },
        {
          subtitle: 'How to Exercise Your Rights',
          content: `To exercise any of these rights:
1. Log into your account and adjust settings where available
2. Email us at hi@togethr.space with your request
3. We may need to verify your identity before fulfilling requests

We will respond within one month, or sooner if possible. There is no fee unless your request is clearly unfounded or excessive.`,
        },
      ],
    },
    {
      title: 'DATA RETENTION',
      id: 'data-retention',
      sections: [
        {
          subtitle: 'Active Accounts',
          content: `While your account is active, we retain your data to provide services.`,
        },
        {
          subtitle: 'After Account Deletion',
          content: `When you delete your account:
● Most data is deleted within 30 days
● Some data may be retained in backup systems for up to 90 days
● We may retain certain data longer to comply with legal obligations (e.g., financial records for 6 years)`,
        },
        {
          subtitle: 'Specific Retention Periods',
          type: 'table',
          tableData: [
            { category: 'Account information', period: 'While account active + 30 days after deletion', reason: 'Service provision' },
            { category: 'Special category data (sexual orientation, gender identity)', period: 'While consent valid + 30 days after withdrawal', reason: 'Consent-based processing' },
            { category: 'Transaction records', period: '6 years after transaction', reason: 'Legal/tax obligations' },
            { category: 'Customer support records', period: '6 years after last contact', reason: 'Legal claims' },
            { category: 'Marketing consent records', period: '3 years after consent or until withdrawal', reason: 'Regulatory requirements' },
            { category: 'Technical logs', period: '90 days', reason: 'Security and troubleshooting' },
            { category: 'Anonymised analytics', period: 'Indefinitely', reason: 'Cannot identify individuals' },
          ],
        },
        {
          subtitle: 'Secure Deletion',
          content: `When we delete data, we use industry-standard secure deletion methods to ensure it cannot be recovered.`,
        },
      ],
    },
    {
      title: 'SECURITY MEASURES',
      id: 'security',
      sections: [
        {
          subtitle: 'Technical Measures',
          content: `● Encryption: Data is encrypted in transit (TLS/SSL) and at rest
● Access controls: Role-based access with multi-factor authentication for staff
● Firewalls and intrusion detection: Network-level security
● Regular security testing: Penetration testing and vulnerability assessments
● Secure development practices: Code reviews and security training for developers`,
        },
        {
          subtitle: 'Organisational Measures',
          content: `● Staff training: All staff receive data protection and LGBTQIA+ sensitivity training
● Limited access: Only authorised personnel can access personal data, on a need-to-know basis
● Confidentiality agreements: All staff and contractors sign confidentiality agreements
● Incident response plan: Procedures for detecting and responding to security breaches`,
        },
        {
          subtitle: 'Data Breach Notification',
          content: `If a data breach occurs that poses a risk to your rights and freedoms:
● We will notify the ICO within 72 hours of becoming aware
● We will notify you without undue delay if the breach is high risk
● We will advise you on steps to protect yourself`,
        },
        {
          subtitle: 'Your Security Responsibilities',
          content: `Please help keep your data secure by:
● Using a strong, unique password
● Enabling two-factor authentication if available
● Not sharing your login credentials
● Logging out from shared devices
● Reporting suspicious activity to us immediately`,
        },
      ],
    },
    {
      title: "CHILDREN'S PRIVACY",
      id: 'children',
      sections: [
        {
          subtitle: 'Age Requirements',
          content: `Our Platform is intended for users aged 18 and older. We do not knowingly collect personal data from children under 18.`,
        },
        {
          subtitle: 'Parental Rights',
          content: `If you are a parent/guardian and believe your child under 18 has provided us with personal data, please contact us immediately at hi@togethr.space. We will delete the data promptly.`,
        },
      ],
    },
    {
      title: 'COOKIES AND SIMILAR TECHNOLOGIES',
      id: 'cookies',
      sections: [
        {
          content: `We use cookies and similar technologies to operate and improve our Platform. For full details, please see our separate Cookie Policy.

In brief:
● Strictly necessary cookies are essential for the Platform to work
● Non-essential cookies (analytics, advertising) require your consent
● You can manage cookie preferences through our cookie banner or settings`,
        },
      ],
    },
    {
      title: 'THIRD-PARTY LINKS AND SERVICES',
      id: 'third-parties',
      sections: [
        {
          content: `Our Platform may contain links to third-party websites, services, or social media platforms (e.g., Instagram, WhatsApp, Facebook). We are not responsible for their privacy practices. We encourage you to read their privacy policies before providing any information.

Be cautious: Sharing LGBTQIA+ identity information on third-party platforms may expose you to risks outside our control.`,
        },
      ],
    },
    {
      title: 'YOUR RIGHTS TO COMPLAIN',
      id: 'complaints',
      sections: [
        {
          content: `We hope to resolve any concerns you have about our data practices. However, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):

Information Commissioner's Office
● Website: https://ico.org.uk/make-a-complaint/
● Phone: 0303 123 1113
● Address: Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF

You also have the right to complain to a supervisory authority in the EU member state of your habitual residence, place of work, or place of alleged infringement if the EU GDPR applies to you.`,
        },
      ],
    },
    {
      title: 'SPECIAL PROTECTIONS FOR LGBTQIA+ COMMUNITY',
      id: 'lgbtq-protection',
      sections: [
        {
          subtitle: 'Our Commitment',
          content: `We recognise that for LGBTQIA+ individuals, privacy isn't just about convenience—it can be a matter of safety, dignity, and fundamental human rights. We commit to:
● Respecting chosen identities: You can use your preferred name, pronouns, and identity descriptors
● Preventing "outing": We will never disclose your LGBTQIA+ status to others without your explicit permission
● Safe spaces: Robust moderation to prevent harassment and hate speech
● Data minimisation: We collect only what's necessary
● Transparency: Clear information about who can see what`,
        },
        {
          subtitle: 'Your Control Over Sensitive Information',
          content: `You have granular control over who sees information about your sexual orientation and gender identity:
● Default setting: Private (only visible to you)
● Options to share with: specific friends, all community members, or publicly
● You can change these settings at any time`,
        },
        {
          subtitle: 'Anonymous Browsing',
          content: `We offer features that allow you to explore our Platform without creating an account or disclosing identifying information, so questioning individuals can access resources safely.`,
        },
        {
          subtitle: 'Emergency Account Lockdown',
          content: `If you face an imminent threat of forced outing or safety concern, you can immediately:
● Hide your profile from public view
● Pause all notifications
● Contact us for emergency account suspension

Contact hi@togethr.space with subject line "URGENT: Safety Concern" for immediate assistance.`,
        },
      ],
    },
    {
      title: 'CHANGES TO YOUR DATA PROCESSING',
      id: 'data-changes',
      sections: [
        {
          content: `If we wish to use your personal data for purposes not covered by this policy, we will:
● Notify you before the new processing begins
● Explain the new purpose and lawful basis
● Seek your consent if required by law
● Give you the opportunity to object or opt out`,
        },
      ],
    },
    {
      title: 'DATA PROTECTION PRINCIPLES',
      id: 'principles',
      sections: [
        {
          content: `We process your personal data in accordance with the following principles:
1. Lawfulness, fairness, and transparency: We process data lawfully, fairly, and in a transparent manner
2. Purpose limitation: We collect data for specified, explicit, and legitimate purposes
3. Data minimisation: We collect only what is adequate, relevant, and necessary
4. Accuracy: We take reasonable steps to ensure data is accurate and up to date
5. Storage limitation: We don't keep data longer than necessary
6. Integrity and confidentiality: We process data securely and protect against unauthorized processing or loss`,
        },
      ],
    },
    {
      title: 'DETAILED DATA PROCESSING TABLE',
      id: 'data-table',
      sections: [
        {
          type: 'full-table',
          tableData: [
            { category: 'Account Information', points: 'Name, email, username, password, date of birth', purpose: 'Account creation and management; authentication; age verification', basis: 'Contractual necessity', retention: 'While account active + 30 days after deletion', recipients: 'Cloud hosting providers' },
            { category: 'Profile Information', points: 'Chosen name, pronouns, bio, location (optional), profile photo', purpose: 'Personalisation; community connection; service provision', basis: 'Contractual necessity; Consent (for optional fields)', retention: 'While account active + 30 days after deletion', recipients: 'Cloud hosting providers' },
            { category: 'Special Category Data', points: 'Sexual orientation, gender identity', purpose: 'Community matching; providing relevant resources; research (anonymised)', basis: 'Explicit consent (can be withdrawn anytime)', retention: 'While consent valid + 30 days after withdrawal', recipients: 'None (never shared without explicit permission)' },
            { category: 'Contact History', points: 'Customer support tickets, feedback, correspondence', purpose: 'Customer support; service improvement; handling complaints', basis: 'Contractual necessity; Legitimate interests (complaints handling)', retention: '6 years after last contact', recipients: 'Customer support software providers' },
            { category: 'Usage Data', points: 'Pages visited, features used, time spent, clicks, searches', purpose: 'Platform improvement; personalisation; analytics; troubleshooting', basis: 'Legitimate interests (improving services)', retention: '90 days (identifiable); Indefinitely (anonymised)', recipients: 'Analytics providers (Google Analytics with IP anonymisation)' },
            { category: 'Technical Data', points: 'IP address, device type, browser type, operating system, device identifiers', purpose: 'Security; fraud prevention; platform optimisation; technical support', basis: 'Legitimate interests (security and service provision)', retention: '90 days', recipients: 'Cloud hosting and security providers' },
            { category: 'Location Data', points: 'Precise geolocation (GPS); approximate location (IP-derived)', purpose: 'Showing nearby events/users (if enabled); region-specific content', basis: 'Consent (precise); Legitimate interests (approximate)', retention: 'While feature in use + 30 days', recipients: 'None (processed internally)' },
            { category: 'Transaction Data', points: 'Purchase history, payment confirmation (not full card details)', purpose: 'Processing purchases; order fulfillment; financial recordkeeping', basis: 'Contractual necessity; Legal obligation (tax/accounting)', retention: '6 years after transaction', recipients: 'Payment processors (Stripe, PayPal)' },
            { category: 'Marketing Preferences', points: 'Email opt-in status, communication preferences', purpose: 'Sending marketing communications; newsletters', basis: 'Consent (can be withdrawn anytime)', retention: '3 years after consent or until withdrawal', recipients: 'Email service providers' },
            { category: 'Survey Responses', points: 'Feedback, opinions, research participation', purpose: 'Product improvement; understanding community needs; research', basis: 'Consent', retention: '3 years after collection (identifiable); Indefinitely (anonymised)', recipients: 'Survey software providers' },
            { category: 'Community Interaction Data', points: 'Posts, comments, messages, event registrations, group memberships', purpose: 'Facilitating community interaction; event management; moderation', basis: 'Contractual necessity', retention: 'While account active + 30 days after deletion (public content may remain anonymised)', recipients: 'None (stored internally)' },
          ],
        },
      ],
    },
    {
      title: 'ADDITIONAL INFORMATION',
      id: 'additional-info',
      sections: [
        {
          subtitle: 'Do Not Track Signals',
          content: `Some browsers include "Do Not Track" (DNT) signals. Currently, there is no universal standard for how to interpret DNT signals. We do not currently respond to DNT signals, but you can control tracking through our cookie settings.`,
        },
        {
          subtitle: 'Automated Decision-Making and Profiling',
          content: `We do not use automated decision-making that produces legal or similarly significant effects. We may use limited profiling for analytics and personalisation (e.g., suggesting relevant events), but this:
● Does not produce legal or significant effects
● Can be opted out of in your privacy settings
● Is never used to discriminate or make consequential decisions about you`,
        },
        {
          subtitle: 'Research and Development',
          content: `We may use anonymised, aggregated data for research to improve services for LGBTQIA+ communities. This data cannot identify you. If we conduct specific research studies involving identifiable data, we will:
● Seek separate, informed consent
● Provide full information about the research
● Allow you to opt out at any time`,
        },
      ],
    },
    {
      title: 'CONTACT US',
      id: 'final-contact',
      sections: [
        {
          content: `For any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact:

TOGETHR SPACE LTD
Data Protection Contact: Kevin Leonce
Email: hi@togethr.space

We aim to respond to all inquiries within 30 days.`,
        },
      ],
    },
    {
      title: 'EFFECTIVE DATE AND VERSION',
      id: 'effective-date',
      sections: [
        {
          content: `This Privacy Policy is effective as of: 4 December 2025
Version: 2.0

Thank you for trusting us with your personal data. Your privacy and safety are our top priorities.`,
        },
      ],
    },
  ];

  const tableOfContents = policyContent.map((item, index) => ({
    id: item.id,
    title: `${index + 1}. ${item.title}`,
  }));

  // Component to render the Detailed Data Processing Table (Section 18)
  const DataProcessingTable = ({ data }) => (
    <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-purple-100">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Category</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Specific Data Points</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Purpose</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Lawful Basis</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Retention Period</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Third-Party Recipients</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 whitespace-normal text-sm font-medium text-gray-900">{row.category}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.points}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.purpose}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.basis}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.retention}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.recipients}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Component for the Specific Retention Periods (Section 9.3)
  const RetentionTable = ({ data }) => (
    <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200 mt-4">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Data Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Retention Period</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Reason</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 whitespace-normal text-sm font-medium text-gray-900">{row.category}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.period}</td>
              <td className="px-4 py-3 whitespace-normal text-sm text-gray-600">{row.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="py-20 bg-purple-600 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl font-extrabold tracking-tight"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-xl text-purple-200"
          >
            TOGETHR SPACE LTD
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 flex flex-wrap gap-4 text-purple-100 text-sm"
          >
            <span className="flex items-center">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
              Last updated: 4 December 2025
            </span>
            <span className="flex items-center">
              <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
              UK GDPR Compliant
            </span>
            <a href="#contact-info" className="flex items-center hover:text-white transition-colors">
              <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
              Contact DPO: Kevin Leonce
            </a>
          </motion.div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8 pt-12">
          {/* Table of Contents (Sidebar) */}
          <div className="lg:col-span-3 lg:sticky lg:top-12 h-fit mb-10 lg:mb-0">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="p-6 bg-gray-50 rounded-xl shadow-lg border border-gray-200"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-purple-600" />
                Table of Contents
              </h3>
              <nav className="space-y-2">
                {tableOfContents.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="block text-sm text-gray-600 hover:text-purple-600 transition-colors py-1 pl-2 border-l-2 border-gray-300 hover:border-purple-600"
                  >
                    {item.title}
                  </a>
                ))}
              </nav>
            </motion.div>
            {/* LGBTQIA+ Commitment CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mt-8 p-6 bg-indigo-600 rounded-xl shadow-xl text-white"
            >
              <h4 className="text-xl font-bold mb-2 flex items-center">
                <SafeIcon icon={FiUsers} className="w-6 h-6 mr-2" />
                Community Safety First
              </h4>
              <p className="text-indigo-200 text-sm">
                We provide special protections for LGBTQIA+ members. Learn about our commitment to preventing "outing" and ensuring safe spaces.
              </p>
              <a href="#lgbtq-protection" className="mt-3 inline-flex items-center text-sm font-medium text-white hover:text-indigo-100 transition-colors">
                Read Special Protections &rarr;
              </a>
            </motion.div>
          </div>

          {/* Policy Content */}
          <div className="lg:col-span-9">
            <div className="divide-y divide-gray-200">
              {policyContent.map((section, sectionIndex) => (
                <Section key={section.id} id={section.id} className="pt-10 pb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 border-b-2 border-purple-200 pb-2">
                    {sectionIndex + 1}. {section.title}
                  </h2>
                  
                  {section.sections.map((sub, subIndex) => (
                    <div key={subIndex} className="mt-6">
                      {sub.subtitle && (
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">
                          {sectionIndex + 1}.{subIndex + 1} {sub.subtitle}
                        </h3>
                      )}
                      
                      {sub.type === 'full-table' && <DataProcessingTable data={sub.tableData} />}
                      {sub.type === 'table' && <RetentionTable data={sub.tableData} />}
                      {sub.content && renderPolicyText(sub.content)}
                    </div>
                  ))}
                </Section>
              ))}
            </div>

            {/* Simple Footer Contact Section */}
            <div className="mt-16 mb-16 p-8 bg-purple-50 rounded-xl border border-purple-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Have Questions?</h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or how we handle your data, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:hi@togethr.space"
                  className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5 mr-2" />
                  Email Us
                </a>
                <a
                  href="https://togethr.space"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-600 font-medium rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors"
                >
                  Visit Our Website
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;